import { APP_URL_CHAT, STORAGE_KEY_HELD_MESSAGE } from '../const';
import StorageService from './StorageService';

/**
 * The message a free-plan customer was blocked from sending (HORO-10679).
 *
 * It has to outlive a full-page round trip — paywall, checkout, confirmation,
 * back to the chat — and `chatsSavedMessages` cannot carry it: ChatBar wipes
 * that map on unmount so the composer feels fresh each visit, and navigating to
 * checkout unmounts ChatBar. Hence a separate record, modelled on
 * `purchaseRecord`.
 *
 * The three stages are the whole safety model. Only `paid` is resumable, so a
 * customer who abandons checkout leaves an inert `committed` record behind
 * rather than one that sends itself the next time they open the chat.
 */
export const HELD_MESSAGE_STAGE = {
    HELD: 'held',
    COMMITTED: 'committed',
    PAID: 'paid'
};

/** Past this the customer has moved on, whatever stage the record reached. */
export const HELD_MESSAGE_TTL_MS = 30 * 60 * 1000;

const CHAT_PATH_PREFIX = `/${APP_URL_CHAT}/`;

/**
 * The chat id in a path, or null when the path is not a specific conversation.
 *
 * `/advisor/zara` is the advisor landing, not a chat — it has no id yet, and
 * `useChatIdByUrl` (last segment, unconditionally) would hand back the alias.
 * Arming a record against `'zara'` would then resume into the wrong place.
 */
export const chatIdFromPathname = (pathname) => {
    if (typeof pathname !== 'string' || !pathname.startsWith(CHAT_PATH_PREFIX)) return null;
    const [alias, chatId] = pathname.slice(CHAT_PATH_PREFIX.length).split('/');
    return alias && chatId ? chatId : null;
};

export const getHeldMessage = () => StorageService.get(STORAGE_KEY_HELD_MESSAGE) || null;

// Always `delete`: the in-memory fallback source writes null rather than
// removing, so `add(key, null)` would leave a falsy-but-present record behind.
export const clearHeldMessage = () => StorageService.delete(STORAGE_KEY_HELD_MESSAGE);

/** Blocked at send. Re-armed on every keystroke while the paywall is open. */
export const armHeldMessage = ({ chatId, userId, content, assets = [] }) => {
    if (!chatId || !userId || typeof content !== 'string' || !content.trim()) return;
    StorageService.add(STORAGE_KEY_HELD_MESSAGE, {
        chatId,
        userId,
        content,
        assets,
        stage: HELD_MESSAGE_STAGE.HELD,
        at: Date.now(),
        committedAt: null,
        paidAt: null
    });
};

/**
 * The customer actually entered checkout from the chat holding the message.
 *
 * Called from `useGoToCheckout`, which every purchase in the app goes through —
 * the pathname check is what stops a subscription bought from the profile or
 * the nav menu from promoting a record armed in some other conversation.
 */
export const commitHeldMessage = (pathname) => {
    const record = getHeldMessage();
    if (!record || record.stage !== HELD_MESSAGE_STAGE.HELD) return;
    if (chatIdFromPathname(pathname) !== record.chatId) return;
    StorageService.add(STORAGE_KEY_HELD_MESSAGE, {
        ...record,
        stage: HELD_MESSAGE_STAGE.COMMITTED,
        committedAt: Date.now()
    });
};

/**
 * A purchase resolved for `chatId`. Only now may the held message send itself.
 *
 * Takes the conversation the customer is being returned to rather than trusting
 * the stage alone: the commit step runs in `useGoToCheckout`, and a payment
 * that returns through a full page load (3DS) can reach the confirmation screen
 * with the record still at `held`. Scoping on the chat gives the same
 * protection the stage was there for — a subscription bought from the profile
 * cannot promote a message held in some unrelated chat — without depending on
 * that earlier write having survived.
 */
export const markHeldMessagePaid = (chatId) => {
    const record = getHeldMessage();
    if (!record || !chatId || record.chatId !== chatId) return;
    if (record.stage === HELD_MESSAGE_STAGE.PAID) return;
    StorageService.add(STORAGE_KEY_HELD_MESSAGE, {
        ...record,
        stage: HELD_MESSAGE_STAGE.PAID,
        paidAt: Date.now()
    });
};

/**
 * Whether this chat should send this record by itself. The single decision
 * point for the auto-send, kept pure so it carries the test weight.
 */
export const isResumableHeldMessage = (record, { chatId, userId, now = Date.now() } = {}) =>
    !!record &&
    record.stage === HELD_MESSAGE_STAGE.PAID &&
    !!chatId &&
    record.chatId === chatId &&
    !!userId &&
    record.userId === userId &&
    typeof record.content === 'string' &&
    !!record.content.trim() &&
    typeof record.paidAt === 'number' &&
    now - record.paidAt < HELD_MESSAGE_TTL_MS;

/**
 * Whether the composer should show the message as held rather than editable.
 *
 * Only `paid`, and only for the short wait between landing back in the chat and
 * the webhook crediting the pack. Not `held` — the customer may still be
 * editing behind the paywall. Not `committed` either: a chat only mounts once
 * they have left checkout, so a committed record means they came back without
 * buying, and holding that composer would strand someone who simply changed
 * their mind.
 */
export const isComposerHeldFor = (record, chatId) =>
    !!record && !!chatId && record.chatId === chatId && record.stage === HELD_MESSAGE_STAGE.PAID;
